import * as d3 from "d3";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type KpiModelNode from "@/_generated/types/serializers/KpiModelNode.ts";

interface TreeNode extends KpiModelNode {
  children?: TreeNode[];
}

interface Props {
  data: TreeNode[];
  onNodeClick?: (nodeId: number, ancestorIds: number[]) => void;
  onLoadChildren: (nodeId: number) => Promise<TreeNode[]>;
}

export interface KpiModelTreeRef {
  expandToNode: (ancestorIds: number[], targetId: number) => Promise<void>;
  clearSelection: () => void;
}

interface D3TreeNode extends d3.HierarchyPointNode<TreeNode> {
  x0?: number;
  y0?: number;
  _children?: D3TreeNode[];
  _loading?: boolean;
  _loaded?: boolean;
}

const LEVEL_COLORS: Record<number, string> = {
  0: "oklch(0.6 0.15 280)",
  1: "oklch(0.75 0.18 347)",
  2: "oklch(0.64 0.10 275)",
  3: "oklch(0.75 0.12 245)",
  4: "oklch(0.71 0.10 187)",
  5: "oklch(0.72 0.16 290)",
};

const NODE_WIDTH = 160;
const NODE_HEIGHT = 36;
const HORIZONTAL_SPACING = 20;
const VERTICAL_SPACING = 80;

const KpiModelTree = forwardRef<KpiModelTreeRef, Props>(function KpiModelTree(
  { data, onNodeClick, onLoadChildren },
  ref,
) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const initializedRef = useRef(false);

  // Keep callback refs updated so D3 event handlers always have current value
  const onNodeClickRef = useRef(onNodeClick);
  onNodeClickRef.current = onNodeClick;
  const onLoadChildrenRef = useRef(onLoadChildren);
  onLoadChildrenRef.current = onLoadChildren;

  // Refs to store D3 functions for external access
  const d3Refs = useRef<{
    root: D3TreeNode | null;
    update: ((source: D3TreeNode) => void) | null;
    zoom: d3.ZoomBehavior<SVGSVGElement, unknown> | null;
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined> | null;
    findNode: ((id: number) => D3TreeNode | undefined) | null;
    loadAndExpandNode: ((node: D3TreeNode) => Promise<void>) | null;
    selectNode: ((nodeId: number | null) => void) | null;
  }>({
    root: null,
    update: null,
    zoom: null,
    svg: null,
    findNode: null,
    loadAndExpandNode: null,
    selectNode: null,
  });

  useImperativeHandle(ref, () => ({
    expandToNode: async (ancestorIds: number[], targetId: number) => {
      const { root, update, findNode, loadAndExpandNode, zoom, svg } =
        d3Refs.current;
      if (!root || !update || !findNode || !loadAndExpandNode || !zoom || !svg)
        return;

      // Expand each ancestor in order
      for (const ancestorId of ancestorIds) {
        const node = findNode(ancestorId);
        if (
          node &&
          !node.children &&
          (node._children || node.data.children_count > 0)
        ) {
          await loadAndExpandNode(node);
        }
      }

      // Find and highlight target node
      const targetNode = findNode(targetId);
      if (targetNode && dimensions) {
        // Pan and zoom to the target node
        const { width, height } = dimensions;
        const scale = 1.5;
        const x = width / 2 - (targetNode.x ?? 0) * scale;
        const y = height / 2 - (targetNode.y ?? 0) * scale;

        svg
          .transition()
          .duration(500)
          .call(zoom.transform, d3.zoomIdentity.translate(x, y).scale(scale));

        // Highlight the node temporarily using CSS class
        const nodeElement = svg.select(`g.node[data-id="${targetId}"]`);
        if (!nodeElement.empty()) {
          nodeElement.classed("highlighted", true);
          setTimeout(() => {
            nodeElement.classed("highlighted", false);
          }, 2000);
        }
      }
    },
    clearSelection: () => {
      d3Refs.current.selectNode?.(null);
    },
  }));

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        setDimensions({ width, height });
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (
      !svgRef.current ||
      !data.length ||
      !dimensions ||
      initializedRef.current
    )
      return;
    initializedRef.current = true;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const { width } = dimensions;
    const margin = { top: 60, right: 40, bottom: 40, left: 40 };

    const virtualRoot: TreeNode = {
      id: 0,
      name: "KPI Model",
      slug: "root",
      level: 0,
      level_name: "root",
      has_rubric: false,
      children_count: data.length,
      children: data,
    };

    const root = d3.hierarchy(virtualRoot) as D3TreeNode;

    root.x0 = 0;
    root.y0 = 0;

    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 3])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });

    svg.call(zoom);

    // Store refs for external access
    d3Refs.current.root = root;
    d3Refs.current.zoom = zoom;
    d3Refs.current.svg = svg;

    // Add CSS animation for spinner
    svg.append("defs").html(`
      <style>
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .loading-spinner {
          animation: spin 1s linear infinite;
          transform-box: fill-box;
          transform-origin: center;
        }
        @keyframes pulse-highlight {
          0%, 100% {
            stroke-width: 2;
            filter: drop-shadow(0 0 0 transparent);
          }
          50% {
            stroke-width: 4;
            filter: drop-shadow(0 0 8px oklch(0.54 0.18 288));
          }
        }
        .highlighted rect:first-of-type {
          animation: pulse-highlight 0.6s ease-in-out 3;
          stroke: oklch(0.54 0.18 288) !important;
        }
        .ancestor-path rect:first-of-type {
          stroke: oklch(0.54 0.18 288) !important;
          stroke-width: 3;
        }
        path.link.ancestor-path {
          stroke: oklch(0.54 0.18 288) !important;
          stroke-width: 2.5;
        }
      </style>
    `);

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const treeLayout = d3
      .tree<TreeNode>()
      .nodeSize([
        NODE_WIDTH + HORIZONTAL_SPACING,
        NODE_HEIGHT + VERTICAL_SPACING,
      ]);

    // Helper to find a node by ID
    const findNode = (id: number): D3TreeNode | undefined => {
      const search = (node: D3TreeNode): D3TreeNode | undefined => {
        if (node.data.id === id) return node;
        const children = node.children || node._children || [];
        for (const child of children) {
          const found = search(child as D3TreeNode);
          if (found) return found;
        }
        return undefined;
      };
      return search(root);
    };

    d3Refs.current.findNode = findNode;

    // Helper to load and expand a node
    const loadAndExpandNode = async (node: D3TreeNode): Promise<void> => {
      if (node.children) return; // Already expanded

      if (node._children) {
        // Already loaded, just expand
        node.children = node._children;
        node._children = undefined;
        update(node);
        return;
      }

      if (node.data.children_count > 0 && !node._loaded) {
        // Need to load from API
        node._loading = true;
        update(node);

        try {
          const childrenData = await onLoadChildrenRef.current(node.data.id);
          node.data.children = childrenData;
          const newChildren = childrenData.map((child) => {
            const childNode = d3.hierarchy(child) as D3TreeNode;
            (childNode as { depth: number }).depth = node.depth + 1;
            (childNode as { parent: D3TreeNode | null }).parent = node;
            return childNode;
          });
          node.children = newChildren;
          node._loaded = true;
        } catch (error) {
          console.error("Failed to load children:", error);
        } finally {
          node._loading = false;
          update(node);
        }
      }
    };

    d3Refs.current.loadAndExpandNode = loadAndExpandNode;

    // Track which node is selected (for persistent highlight)
    let selectedNodeId: number | null = null;

    // Helper to get ancestor IDs from a node
    const getAncestorIds = (node: D3TreeNode): number[] => {
      const ids: number[] = [];
      let current = node.parent;
      while (current) {
        ids.push(current.data.id);
        current = current.parent as D3TreeNode | null;
      }
      return ids;
    };

    // Highlight ancestry chain
    const highlightAncestry = (node: D3TreeNode) => {
      const ancestorIds = getAncestorIds(node);
      // Add class to current node
      svg
        .select(`g.node[data-id="${node.data.id}"]`)
        .classed("ancestor-path", true);
      // Add class to ancestors
      for (const id of ancestorIds) {
        svg.select(`g.node[data-id="${id}"]`).classed("ancestor-path", true);
      }
      // Highlight links where target is in the path
      const pathIds = [node.data.id, ...ancestorIds];
      svg
        .selectAll<SVGPathElement, d3.HierarchyPointLink<TreeNode>>("path.link")
        .classed("ancestor-path", (d) => pathIds.includes(d.target.data.id));
    };

    // Highlight by node ID (used when selecting a node)
    const highlightAncestryById = (nodeId: number) => {
      const node = findNode(nodeId);
      if (node) {
        highlightAncestry(node);
      }
    };

    // Clear ancestry highlight
    const clearAncestryHighlight = () => {
      svg.selectAll(".ancestor-path").classed("ancestor-path", false);
    };

    // Select a node (highlight its ancestry)
    const selectNode = (nodeId: number | null) => {
      selectedNodeId = nodeId;
      clearAncestryHighlight();
      if (nodeId) {
        highlightAncestryById(nodeId);
      }
    };

    d3Refs.current.selectNode = selectNode;

    const update = (source: D3TreeNode) => {
      d3Refs.current.update = update;
      const duration = 300;

      const treeData = treeLayout(root);
      const nodes = treeData.descendants() as D3TreeNode[];
      const links = treeData.links();

      nodes.forEach((d) => {
        d.y = d.depth * (NODE_HEIGHT + VERTICAL_SPACING);
      });

      // ========== NODES ==========
      const node = g
        .selectAll<SVGGElement, D3TreeNode>("g.node")
        .data(nodes, (d) => String(d.data.id));

      const nodeEnter = node
        .enter()
        .append("g")
        .attr("class", "node")
        .attr("data-id", (d) => d.data.id)
        .attr(
          "transform",
          () => `translate(${source.x0 ?? 0},${source.y0 ?? 0})`,
        )
        .style("cursor", "pointer")
        .on("click", (_event, d) => {
          // Click opens detail panel and highlights ancestry (except for root)
          if (d.data.level > 0) {
            selectNode(d.data.id);
            onNodeClickRef.current?.(d.data.id, getAncestorIds(d));
          }
        })
        .on("mouseenter", (_event, d) => {
          // Only show hover highlight if no node is selected
          if (selectedNodeId === null && d.data.level > 0) {
            highlightAncestry(d);
          }
        })
        .on("mouseleave", () => {
          // Only clear if no node is selected
          if (selectedNodeId === null) {
            clearAncestryHighlight();
          }
        });

      nodeEnter
        .append("rect")
        .attr("width", NODE_WIDTH)
        .attr("height", NODE_HEIGHT)
        .attr("x", -NODE_WIDTH / 2)
        .attr("y", -NODE_HEIGHT / 2)
        .attr("rx", 6)
        .attr("ry", 6)
        .attr("fill", "oklch(0.98 0.01 280)")
        .attr("stroke", (d) => LEVEL_COLORS[d.data.level] || "#666")
        .attr("stroke-width", 2);

      nodeEnter
        .append("rect")
        .attr("width", 4)
        .attr("height", NODE_HEIGHT - 8)
        .attr("x", -NODE_WIDTH / 2 + 4)
        .attr("y", -NODE_HEIGHT / 2 + 4)
        .attr("rx", 2)
        .attr("fill", (d) => LEVEL_COLORS[d.data.level] || "#666");

      nodeEnter
        .append("text")
        .attr("dy", "0.35em")
        .attr("x", -NODE_WIDTH / 2 + 14)
        .attr("text-anchor", "start")
        .attr("font-size", "10px")
        .attr("font-weight", "500")
        .attr("fill", "#333")
        .text((d) => {
          const name = d.data.name;
          return name.length > 20 ? `${name.substring(0, 18)}...` : name;
        })
        .append("title")
        .text((d) => d.data.name);

      nodeEnter
        .filter((d) => d.data.level > 0)
        .append("text")
        .attr("x", NODE_WIDTH / 2 - 20)
        .attr("y", -NODE_HEIGHT / 2 + 11)
        .attr("font-size", "8px")
        .attr("font-weight", "600")
        .attr("fill", (d) => LEVEL_COLORS[d.data.level] || "#666")
        .text((d) => `L${d.data.level}`);

      // Rubric indicator (visual only - clicking node opens panel)
      nodeEnter
        .filter((d) => d.data.has_rubric)
        .append("circle")
        .attr("cx", NODE_WIDTH / 2 - 8)
        .attr("cy", NODE_HEIGHT / 2 - 8)
        .attr("r", 3)
        .attr("fill", "oklch(0.72 0.21 142)");

      // Spinner for loading state
      nodeEnter
        .append("circle")
        .attr("class", "loading-spinner")
        .attr("cx", NODE_WIDTH / 2 - 8)
        .attr("cy", 0)
        .attr("r", 6)
        .attr("fill", "none")
        .attr("stroke", (d) => LEVEL_COLORS[d.data.level] || "#666")
        .attr("stroke-width", 2)
        .attr("stroke-dasharray", "12 6")
        .attr("opacity", (d) => (d._loading ? 1 : 0));

      // Expand/collapse button (clickable)
      const expandButton = nodeEnter
        .filter(
          (d) => !!(d.children || d._children || d.data.children_count > 0),
        )
        .append("g")
        .attr("class", "expand-button")
        .style("cursor", "pointer")
        .on("click", async (event, d) => {
          event.stopPropagation();
          if (d.children) {
            d._children = d.children as D3TreeNode[];
            d.children = undefined;
            update(d);
          } else if (d._children) {
            d.children = d._children;
            d._children = undefined;
            update(d);
          } else if (d.data.children_count > 0 && !d._loaded && !d._loading) {
            d._loading = true;
            update(d);
            try {
              const childrenData = await onLoadChildrenRef.current(d.data.id);
              d.data.children = childrenData;
              const newChildren = childrenData.map((child) => {
                const childNode = d3.hierarchy(child) as D3TreeNode;
                (childNode as { depth: number }).depth = d.depth + 1;
                (childNode as { parent: D3TreeNode | null }).parent = d;
                return childNode;
              });
              d.children = newChildren;
              d._loaded = true;
            } catch (error) {
              console.error("Failed to load children:", error);
            } finally {
              d._loading = false;
              update(d);
            }
          }
        });

      // Expand button background (larger click area)
      expandButton
        .append("rect")
        .attr("x", NODE_WIDTH / 2 - 16)
        .attr("y", -8)
        .attr("width", 16)
        .attr("height", 16)
        .attr("fill", "transparent");

      // Expand indicator text
      expandButton
        .append("text")
        .attr("class", "expand-indicator")
        .attr("x", NODE_WIDTH / 2 - 8)
        .attr("y", 3)
        .attr("text-anchor", "middle")
        .attr("font-size", "12px")
        .attr("font-weight", "bold")
        .attr("fill", "#666")
        .attr("opacity", (d) => (d._loading ? 0 : 1))
        .text((d) => {
          if (d.children) return "−";
          if (d._children || d.data.children_count > 0) return "+";
          return "";
        });

      const nodeUpdate = nodeEnter.merge(node);

      nodeUpdate
        .transition()
        .duration(duration)
        .attr("transform", (d) => `translate(${d.x},${d.y})`);

      nodeUpdate
        .select(".expand-indicator")
        .attr("opacity", (d) => (d._loading ? 0 : 1))
        .text((d) => {
          if (d.children) return "−";
          if (d._children || d.data.children_count > 0) return "+";
          return "";
        });

      nodeUpdate
        .select(".loading-spinner")
        .attr("opacity", (d) => (d._loading ? 1 : 0));

      node
        .exit()
        .transition()
        .duration(duration)
        .attr("transform", () => `translate(${source.x},${source.y})`)
        .remove();

      // ========== LINKS ==========
      const link = g
        .selectAll<SVGPathElement, d3.HierarchyPointLink<TreeNode>>("path.link")
        .data(links, (d) => String(d.target.data.id));

      const linkEnter = link
        .enter()
        .insert("path", "g")
        .attr("class", "link")
        .attr("fill", "none")
        .attr("stroke", "#ccc")
        .attr("stroke-width", 1.5)
        .attr("d", () => {
          const o = { x: source.x0 ?? 0, y: source.y0 ?? 0 };
          return diagonal({ source: o, target: o });
        });

      linkEnter
        .merge(link)
        .transition()
        .duration(duration)
        .attr("d", (d) =>
          diagonal({
            source: { x: d.source.x, y: d.source.y },
            target: { x: d.target.x, y: d.target.y },
          }),
        );

      link
        .exit()
        .transition()
        .duration(duration)
        .attr("d", () => {
          const o = { x: source.x, y: source.y };
          return diagonal({ source: o, target: o });
        })
        .remove();

      nodes.forEach((d) => {
        d.x0 = d.x;
        d.y0 = d.y;
      });
    };

    const diagonal = (d: {
      source: { x: number; y: number };
      target: { x: number; y: number };
    }) => {
      return `M${d.source.x},${d.source.y + NODE_HEIGHT / 2}
              C${d.source.x},${(d.source.y + d.target.y) / 2}
               ${d.target.x},${(d.source.y + d.target.y) / 2}
               ${d.target.x},${d.target.y - NODE_HEIGHT / 2}`;
    };

    update(root);

    const initialTransform = d3.zoomIdentity.translate(width / 2, margin.top);
    svg.call(zoom.transform, initialTransform);
  }, [data, dimensions]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full bg-card rounded-lg border border-border overflow-hidden"
    >
      <svg
        ref={svgRef}
        width={dimensions?.width ?? "100%"}
        height={dimensions?.height ?? "100%"}
        className="block"
      />
    </div>
  );
});

export default KpiModelTree;
